/**
 * Data-fetching hooks.
 *
 * No data-fetching library: the app has one consistent API shape, and these
 * two hooks cover every screen in it. What they do handle properly is the
 * part hand-rolled fetching usually gets wrong —
 *
 *  - aborting an in-flight request when the component unmounts or the inputs
 *    change, so a slow response cannot overwrite a newer one;
 *  - keeping the previous data visible during a refetch, so tables do not
 *    flash empty;
 *  - distinguishing the FIRST load (show a skeleton) from a REFETCH (show a
 *    thin progress bar).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * @param {Function} fetcher async (signal) => data
 * @param {Array}    deps    re-runs when these change
 * @param {object}   [options]
 * @param {boolean}  [options.enabled] skip the fetch entirely when false
 * @param {*}        [options.initialData]
 */
export function useApi(fetcher, deps = [], options = {}) {
  const { enabled = true, initialData = null } = options;

  const [data, setData] = useState(initialData);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [refetching, setRefetching] = useState(false);

  const hasLoaded = useRef(false);
  const controller = useRef(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useCallback(
    async () => {
      if (!enabled) {
        setLoading(false);
        return;
      }

      // Cancel whatever is in flight: its response is now stale, and letting
      // it land would overwrite the newer result.
      controller.current?.abort();
      const current = new AbortController();
      controller.current = current;

      // A refetch with nothing loaded yet ("Try again" after a failed first
      // load) is still a first load: it must show the loading state, or the
      // screen renders with error cleared and data still null.
      if (hasLoaded.current) setRefetching(true);
      else setLoading(true);
      setError(null);

      try {
        const result = await fetcherRef.current(current.signal);
        if (current.signal.aborted) return;
        setData(result);
        hasLoaded.current = true;
      } catch (err) {
        if (current.signal.aborted || err.name === 'AbortError') return;
        setError(err);
      } finally {
        if (!current.signal.aborted) {
          setLoading(false);
          setRefetching(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, ...deps]
  );

  useEffect(() => {
    run();
    return () => controller.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run]);

  return {
    data,
    error,
    /** True only on the first load, so a skeleton shows once. */
    loading: loading && !hasLoaded.current,
    /** True on a background refresh, for a subtle progress indicator. */
    refetching,
    isEmpty: !loading && !error && (data === null || (Array.isArray(data) && data.length === 0)),
    refetch: () => run(),
    setData,
  };
}

/**
 * A mutation: an action with pending state and error handling, so a submit
 * button can disable itself without every form re-implementing that.
 */
export function useMutation(mutator, { onSuccess, onError } = {}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  const mutate = useCallback(
    async (...args) => {
      setPending(true);
      setError(null);
      try {
        const result = await mutator(...args);
        onSuccess?.(result);
        return result;
      } catch (err) {
        // Recorded BEFORE onError and before the rethrow, so a caller that
        // swallows the rejection still leaves the form something to render.
        setError(err);
        onError?.(err);
        // Rethrown so a caller can branch, but the state is already set.
        throw err;
      } finally {
        // ALWAYS, on every path. `pending` is what disables the submit button
        // and shows its spinner, so anything that can leave it set is a button
        // that never comes back - which is exactly what a user reads as "the
        // app hung". A `finally` is the only version of this that survives a
        // failure mode nobody thought of.
        //
        // There is deliberately no is-still-mounted guard. The one that used to
        // be here was a ref cleared by an unmount cleanup and never restored on
        // remount, so React's StrictMode double-invoke left it false from the
        // first render onward - and in development NOTHING below it ever ran:
        // no error, no spinner reset. Setting state on an unmounted component
        // is a no-op in React 18, not a warning, so the guard bought nothing
        // and cost the two updates that matter most when a request fails.
        setPending(false);
      }
    },
    [mutator, onSuccess, onError]
  );

  return { mutate, pending, error, setError, fieldErrors: error?.fieldErrors ?? {} };
}
